// Access mode — ASK-066 lockdown directive (Shaun, 2026-06-12).
//
// Built as a REVERSIBLE FLIP, not a structure: one build-time env var decides
// the surface. Reopening = change the env value + redeploy. No routes, code
// paths, or auth wiring are removed.
//
//   VITE_ACCESS_MODE = "locked"     → prod: holding page only. No auth, no
//                                     boot fetch, no shop surface mounts.
//   VITE_ACCESS_MODE = "allowlist"  → dev: full UX; account creation is
//                                     gated server-side by GCG's beforeCreate
//                                     allowlist — this module only supplies
//                                     the friendly "request access" copy when
//                                     the IdP rejects.
//   (unset / anything else)        → open (pre-directive behaviour).
//
// The IdP can't distinguish dev from prod (one shared auth project — ASK-066
// verified finding), so the "completely locked" half MUST be app-layer: this.

export const ACCESS_MODE: string = (() => {
	try {
		const raw = import.meta.env.VITE_ACCESS_MODE;
		if (typeof raw === "string") return raw.toLowerCase();
	} catch { /* not under Vite */ }
	return "";
})();

export const isLocked = () => ACCESS_MODE === "locked";
export const isAllowlist = () => ACCESS_MODE === "allowlist";

/** Render the prod holding page. Terminal — caller returns immediately after. */
export function renderHoldingPage(): void {
	// ASK-135: the holding page is chrome and draws from the token sheet, which the
	// document <head> links — so it survives this body swap.
	document.body.innerHTML = `
		<div class="kapowx-overlay-surface">
			<div class="kapowx-overlay-card">
				<div class="kapowx-overlay-mark">🌌</div>
				<h1>Shop The Galaxy</h1>
				<p>
					We're building something different — immersive 3D shops you walk through, not scroll past.
					We're currently in a private beta while the first stores are stocked.
				</p>
				<p>
					Interested in early access or opening a shop here?<br>
					<a href="mailto:manager@kapowx.co.za">manager@kapowx.co.za</a>
				</p>
			</div>
		</div>`;
	document.title = "Shop The Galaxy — private beta";
}

/**
 * Allowlist-rejection copy. Firebase surfaces a beforeCreate/beforeSignIn
 * blocking-function rejection as `auth/internal-error` (the function's own
 * message is embedded) or `auth/admin-restricted-operation`. In allowlist
 * mode those almost always mean "not on the list" — say so kindly instead of
 * "something went wrong".
 */
export function allowlistRejectionCopy(code: string): string | null {
	if (!isAllowlist()) return null;
	if (code === "auth/internal-error" || code === "auth/admin-restricted-operation") {
		return "Access is invite-only during our testing phase. If you should have access, ask the team to add your email — then try again.";
	}
	return null;
}
